/**
 * Settings → Account → Your plan: what it includes and how much is used (files, included AI), choosing
 * a plan (Stripe Checkout), and Stripe's billing page to change plan, card or cancel.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard, GraduationCap, LoaderCircle } from "lucide-react";
import { PLANS, SELF_SERVE_PLANS, STUDENT_DISCOUNT, formatTimestamp, planById } from "@brainlog/shared";
import { ApiError, errorMessage, get, post } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { openSettings } from "@/lib/store";
import { OfferBanner, currentOffer } from "./offer";

export interface BillingInfo {
  enabled: boolean;
  plan: string;
  planSource: string | null;
  planNote: string | null;
  period: "month" | "year" | null;
  status: string | null;
  renewsAt: string | null;
  endsAt: string | null;
  subscribed: boolean;
  canManage: boolean;
  basePlan: string | null;
  isAdmin: boolean;
  studentEligible: boolean;
  usage: {
    storageBytes: number;
    storageLimitBytes: number | null;
    aiIncluded: boolean;
    aiToday: number;
    aiPerDay: number | null;
    aiMonth: number;
    aiCreditsPerMonth: number | null;
    extraCredits: number;
  };
}

export const billingKey = ["billing"] as const;
export const useBilling = () => useQuery({ queryKey: billingKey, queryFn: () => get<BillingInfo>("/api/billing") });

const SOURCE: Record<string, string> = {
  complimentary: "Free for life",
  legacy_subscription: "Kept from the previous BrainLog",
  admin: "Administrator",
  stripe: "Paid subscription",
};

function size(bytes: number) {
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(bytes < 100 * 1024 ** 2 ? 1 : 0)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function Meter({ label, used, total, text }: { label: string; used: number; total: number | null; text: string }) {
  const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span>{label}</span>
        <span className={cn("text-muted-foreground", pct >= 100 && "font-medium text-destructive", pct >= 80 && pct < 100 && "text-warning")}>{text}</span>
      </div>
      {total ? (
        <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="meter" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className={cn("h-full rounded-full bg-primary", pct >= 80 && "bg-warning", pct >= 100 && "bg-destructive")} style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}

/** Go to Stripe: Checkout for a plan, or the billing page when a subscription already exists. */
export async function choosePlan(plan: string, period: "month" | "year", student = false) {
  try {
    // An offer code (from an announcement's link, or typed) applies to the plan chosen.
    const offer = student ? null : currentOffer();
    const { url } = await post<{ url: string }>("/api/billing/checkout", { plan, period, student, offer });
    track("checkout_start", { plan, period, student });
    window.location.assign(url);
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) return openBillingPage();
    toast.error(errorMessage(e));
  }
}

export async function openBillingPage() {
  try {
    const { url } = await post<{ url: string }>("/api/billing/portal", {});
    window.location.assign(url);
  } catch (e) {
    toast.error(errorMessage(e));
  }
}

export function PlanSettings() {
  const me = useMe();
  const q = useBilling();
  const [yearly, setYearly] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading your plan…</p>;
  if (q.isError || !q.data) return <p className="text-sm text-destructive">{errorMessage(q.error)}</p>;
  const b = q.data;
  const plan = planById(b.plan);
  const tz = me.data?.timezone;
  const go = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    await fn();
    setBusy(null);
  };
  const others = SELF_SERVE_PLANS.map((id) => PLANS.find((p) => p.id === id)!).filter((p) => p.id !== b.plan);

  return (
    <div className="space-y-4 rounded-md border p-3 text-sm">
      <div>
        <p>
          Your plan: <strong>{plan.name}</strong>
          {b.planSource && SOURCE[b.planSource] && <span className="text-muted-foreground"> · {SOURCE[b.planSource]}</span>}
          {b.period && <span className="text-muted-foreground"> · paid {b.period === "year" ? "yearly" : "monthly"}</span>}
        </p>
        {b.planNote && <p className="text-xs text-muted-foreground">{b.planNote}</p>}
        {b.status === "past_due" && (
          <p className="text-xs text-destructive" role="alert">
            The last payment did not go through. Update your card in the billing page to keep your plan.
          </p>
        )}
        {b.endsAt && <p className="text-xs text-muted-foreground">Cancelled: your plan stays until {formatTimestamp(b.endsAt, tz)}, then {planById(b.basePlan ?? "free").name} applies.</p>}
        {b.renewsAt && !b.endsAt && <p className="text-xs text-muted-foreground">Renews on {formatTimestamp(b.renewsAt, tz)}.</p>}
      </div>

      <div className="space-y-3">
        <Meter
          label="Files"
          used={b.usage.storageBytes}
          total={b.usage.storageLimitBytes}
          text={b.usage.storageLimitBytes ? `${size(b.usage.storageBytes)} of ${size(b.usage.storageLimitBytes)}` : `${size(b.usage.storageBytes)} (no limit)`}
        />
        {b.usage.aiIncluded && b.usage.aiCreditsPerMonth != null && (
          <Meter label="Included AI this month" used={b.usage.aiMonth} total={b.usage.aiCreditsPerMonth} text={`${b.usage.aiMonth} of ${b.usage.aiCreditsPerMonth} credits (one “Go in depth” reading uses 4)`} />
        )}
        {b.usage.aiIncluded && b.usage.aiPerDay != null && <Meter label="Included AI today" used={b.usage.aiToday} total={b.usage.aiPerDay} text={`${b.usage.aiToday} of ${b.usage.aiPerDay} actions`} />}
        {b.usage.extraCredits > 0 && (
          <p className="text-xs">
            <strong>{b.usage.extraCredits} extra AI credits</strong> from invitations, used when your plan's included AI is used up.{" "}
            <button type="button" className="text-primary underline" onClick={() => openSettings({ section: "invite" })}>
              Invite more colleagues
            </button>
          </p>
        )}
        <p className="text-xs text-muted-foreground">AI used with your own key or linked ChatGPT subscription is never counted against BrainLog AI credits.</p>
      </div>

      {b.isAdmin ? (
        <p className="text-xs text-muted-foreground">Administrator: no limits apply.</p>
      ) : !b.enabled ? (
        <p className="text-xs text-muted-foreground">
          Payments open soon. To change your plan now, write to <a className="text-primary underline" href="mailto:contact@brainlog.net">contact@brainlog.net</a>.
        </p>
      ) : b.subscribed ? (
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => void go("portal", openBillingPage)}>
          {busy === "portal" ? <LoaderCircle className="animate-spin" /> : <CreditCard />} Change plan, card or cancel
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium">Choose a plan</p>
            <div role="group" aria-label="Billing period" className="inline-flex rounded-full border p-0.5 text-xs">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  aria-pressed={yearly === v}
                  onClick={() => setYearly(v)}
                  className={cn("rounded-full px-3 py-1", yearly === v ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
                >
                  {v ? "Yearly" : "Monthly"}
                </button>
              ))}
            </div>
          </div>
          <OfferBanner allowEntry />
          <ul className="grid gap-2 sm:grid-cols-3">
            {others.map((p) => {
              const price = yearly ? `€${Math.round(p.yearly! / 12)}/month · €${p.yearly}/year` : `€${p.monthly}/month`;
              return (
                <li key={p.id} className="flex flex-col rounded-md border p-2">
                  <span className="font-medium">{p.name}</span>
                  <span className="text-xs text-muted-foreground">{p.storageGb} GB{p.aiCreditsPerMonth ? " · AI included" : ""}{p.forwardingAddress ? " · forwarding address" : ""}</span>
                  <span className="mt-1 text-xs">{price}</span>
                  <Button size="xs" className="mt-2" disabled={!!busy} onClick={() => void go(p.id, () => choosePlan(p.id, yearly ? "year" : "month"))}>
                    {busy === p.id && <LoaderCircle className="animate-spin" />} Choose {p.name}
                  </Button>
                </li>
              );
            })}
          </ul>
          {b.studentEligible && b.plan !== "researcher" && (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => void go("student", () => choosePlan("researcher", "year", true))}>
              {busy === "student" ? <LoaderCircle className="animate-spin" /> : <GraduationCap />} Researcher at the student price, €{STUDENT_DISCOUNT.yearly} a year
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            Payment by Stripe; prices include VAT. Change or cancel at any time from the billing page. A research group?{" "}
            <a className="text-primary underline" href="mailto:contact@brainlog.net?subject=BrainLog%20Lab%20plan">
              Ask about Lab
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}

/** Back from Stripe (?billing=success|cancelled|portal): say what happened and refresh the plan. */
export function useBillingReturn() {
  const qc = useQueryClient();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const what = params.get("billing");
    if (!what) return;
    params.delete("billing");
    window.history.replaceState({}, "", `${window.location.pathname}${params.size ? `?${params}` : ""}`);
    if (what === "cancelled") {
      track("checkout_cancel");
      toast.message("No payment was made: your plan is unchanged.");
      return;
    }
    let tries = 0;
    const check = async () => {
      const b = await get<BillingInfo>("/api/billing").catch(() => null);
      // Stripe confirms the payment a moment after the redirect.
      if (b && (what !== "success" || b.subscribed)) {
        qc.setQueryData(billingKey, b);
        await qc.invalidateQueries({ queryKey: ["me"] });
        if (what === "success") {
          track("checkout_done", { plan: b.plan, period: b.period ?? "" });
          toast.success(`Thank you! Your plan is now ${planById(b.plan).name}.`);
        }
        return;
      }
      if (++tries < 15) setTimeout(() => void check(), 2000);
      else if (what === "success") toast.message("Payment received. Your plan updates in a moment: reload the page if it does not.");
    };
    void check();
  }, [qc]);
}
